import { FileText, MoreVertical, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDocumentRefresh } from '@/hooks/useDocumentRefresh';
import { useTemplateListQuery } from '@/services/api/generated/templatesV2Service';
import {
  useDeleteDocumentMutation,
  useGetMatterDocumentsQuery,
  useLazyDownloadDocumentQuery,
} from '@/services/api/lettersService';
import { DetailedMatter } from '@/services/types/client-intake-types';
import {
  DocumentInstance,
  DocumentTemplate,
} from '@/services/types/letters-types';

import { CreateLetter } from '../letters/CreateLetter';
import { PreviewLetter } from '../letters/PreviewLetter';
import { MatterSectionCard } from './MatterSectionCard';

interface LettersCardProps {
  matter: DetailedMatter;
}

interface LetterRowProps {
  letter: DocumentInstance;
  matter: DetailedMatter;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const getStatusLabel = (status: string, createdAt: string) => {
  const date = formatDate(createdAt);
  switch (status) {
    case 'COMPLETED':
    case 'SIGNED':
      return `Signed: ${date}`;
    case 'SENT':
      return `Sent: ${date}`;
    case 'VOIDED':
      return `Voided: ${date}`;
    default:
      return `Created: ${date}`;
  }
};

const LetterRow = ({ letter, matter }: LetterRowProps) => {
  const [showPreview, setShowPreview] = useState(false);
  const [downloadDocument] = useLazyDownloadDocumentQuery();
  const [deleteDocument, { isLoading: isDeleting }] =
    useDeleteDocumentMutation();

  const isContract = letter.document_template.requires_signatures;
  const showOnlyDownload = !isContract && letter.external_document_reference === 'N/A';

  const handleDownload = async () => {
    try {
      const result = await downloadDocument({
        matterId: matter.id,
        documentInstanceId: letter.id,
      });

      if ('data' in result && result.data instanceof Blob) {
        const blob = result.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${letter.document_template.name}.docx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to download document:', error);
      toast.error('Failed to download document');
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm('Are you sure you want to delete this document?') // eslint-disable-line no-alert
    ) {
      return;
    }

    try {
      await deleteDocument({
        matterId: matter.id,
        documentInstanceId: letter.id,
      }).unwrap();
      toast.success('Document deleted successfully');
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error('Failed to delete document');
    }
  };

  const statusLabel = getStatusLabel(letter.status, letter.created_at);

  return (
    <>
      <div className="flex items-center justify-between py-3 px-4 bg-zinc-50 rounded-lg">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-zinc-900">
            {letter.document_template.name}
          </p>
          <p className="text-xs font-medium text-zinc-500 mt-0.5">
            {statusLabel}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!showOnlyDownload && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPreview(true)}
              className="h-8 px-3 text-xs"
            >
              View
            </Button>
          )}
          {showOnlyDownload && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              className="h-8 px-3 text-xs"
            >
              Download
            </Button>
          )}
          {!isContract && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!showOnlyDownload && (
                  <DropdownMenuItem onClick={handleDownload}>
                    Download
                  </DropdownMenuItem>
                )}
                {letter.detailed_agreement_info?.external_url && (
                  <DropdownMenuItem
                    onClick={() =>
                      window.open(
                        letter.detailed_agreement_info!.external_url,
                        '_blank',
                      )
                    }
                  >
                    Open in Adobe Sign
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="text-red-600"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <PreviewLetter
        open={showPreview}
        onClose={() => setShowPreview(false)}
        document={letter}
        matter={matter}
      />
    </>
  );
};

export const LettersCard = ({ matter }: LettersCardProps) => {
  const { data: letters, isLoading } = useGetMatterDocumentsQuery({
    matterId: matter.id,
  });
  const { data: documentTemplatesData } = useTemplateListQuery({
    firmId: matter.firm.id,
    scope: 'FIRM',
    status: 'ACTIVE',
  });
  const documentTemplates = (documentTemplatesData?.results as unknown as DocumentTemplate[] | undefined)?.filter(t => t.format !== 'EXTERNAL');

  const [selectedTemplate, setSelectedTemplate] =
    useState<DocumentTemplate | null>(null);

  const letterIds = useMemo(() => letters?.map((l) => l.id) ?? [], [letters]);
  useDocumentRefresh(matter.id, letterIds, 30000);

  const sortedLetters = useMemo(() => {
    return [...(letters || [])].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  }, [letters]);

  const handleTemplateSelect = (template: DocumentTemplate) => {
    setSelectedTemplate(template);
  };

  return (
    <>
      <MatterSectionCard
        title="Letters & Correspondence"
        icon={<FileText className="h-4 w-4" />}
        isLoading={isLoading}
        headerActions={
          documentTemplates && documentTemplates.length > 0 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs text-primary hover:text-primary/80"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Add
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {documentTemplates.map((template) => (
                  <DropdownMenuItem
                    key={template.id}
                    onClick={() => handleTemplateSelect(template)}
                  >
                    {template.name}
                    {template.requires_signatures && (
                      <span className="ml-2 text-xs text-zinc-400">
                        (Signature)
                      </span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : undefined
        }
      >
        {sortedLetters.length > 0 ? (
          <div className="space-y-2">
            {sortedLetters.map((letter) => (
              <LetterRow key={letter.id} letter={letter} matter={matter} />
            ))}
          </div>
        ) : (
          <p className="text-sm font-medium text-zinc-500">
            No letters created yet
          </p>
        )}
      </MatterSectionCard>

      {selectedTemplate && (
        <CreateLetter
          open={Boolean(selectedTemplate)}
          onClose={() => setSelectedTemplate(null)}
          matter_id={matter.id}
          matter={matter}
          template={selectedTemplate}
          firm_id={matter.firm.id}
        />
      )}
    </>
  );
};
